import * as React from 'react';
import classnames from 'classnames';
import { isEmpty, isNil, sumBy } from 'lodash';

import { __t, applyTranslations } from 'utils/language';
import { abbrNum, getPercentage } from 'utils/helper/common';

import type { BarChartSimpleItem } from 'types';

applyTranslations(require('./bar-chart-simple.i18n.json'));

type Props = {
  items: Array<BarChartSimpleItem>,
  titles: Array<string>,
  color?: string,
  colors?: Array<string>,
  showPercentMarker?: boolean,
  hideLabelColumn?: boolean,
  hideBarColumn?: boolean,
  hideValueColumn?: boolean,
  modifier?: string,
  isAbbreviated?: boolean
};

const renderEmptyMessage = (): JSX.Element => (
  <div className="bar-chart-simple__no-data">
    <span>
      {__t('bar_chart_simple.no_data')}
    </span>
  </div>
);

const BarChartSimple = (
  {
    items = [],
    titles = [],
    color = '#c5c5c5',
    colors = [],
    showPercentMarker = false,
    hideLabelColumn = false,
    hideBarColumn = false,
    hideValueColumn = false,
    modifier = '',
    isAbbreviated
  }: Props
): JSX.Element => {
  const renderTitles = () => (titles.length > 0 ?
    <div className="bar-chart-simple__item bar-chart-simple__item-titles">
      {
        !isEmpty(items) && titles.map((title, index) => (
          <div
            key={`bar-chart-simple__item--${title}-${index}`}
            className="bar-chart-simple__item-cell bar-chart-simple__item-cell--label text-overflow-ellipsis"
            title={title}
          >
            {title}
          </div>
        ))
      }
    </div>
    : null);

  const renderItemContents = (item: BarChartSimpleItem, index: number): JSX.Element => {
    const totalCount = sumBy(items, 'value');
    const percentage = getPercentage(Number(item.value ?? 0), totalCount);
    const width = percentage < 2 ? '2%' : `${percentage}%`;
    const backgroundColor = colors && colors.length >= items.length ? colors[index] : color;

    return (
      <React.Fragment>
        {
          item.label && !hideLabelColumn && (
            <div
              className="bar-chart-simple__item-cell bar-chart-simple__item-cell--label text-overflow-ellipsis"
              title={item.label}
            >
              {item.label}
            </div>
          )
        }
        <div className="bar-chart-simple__item-cell bar-chart-simple__item-cell--bar">
          <div
            className="bar-chart-simple__bar"
            style={!hideBarColumn ? { width, backgroundColor } : { width }}
          />
        </div>
        {
          !isNil(item.additionalMetric) && (
            <div
              className="bar-chart-simple__item-cell bar-chart-simple__item-cell--value"
              title={String(item.additionalMetric)}
            >
              {isAbbreviated ? abbrNum(item.additionalMetric, { decimal: 1 }, true) : item.additionalMetric}
            </div>
          )
        }
        {
          !isNil(item.value) && !hideValueColumn && (
            <div
              className="bar-chart-simple__item-cell bar-chart-simple__item-cell--value"
              title={`${item.value}${showPercentMarker ? '%' : ''}`}
            >
              {`${item.value}${showPercentMarker ? '%' : ''}`}
            </div>
          )
        }
      </React.Fragment>
    );
  };

  const renderItem = (item: BarChartSimpleItem, index: number): JSX.Element => {
    const itemContainerClasses = classnames('bar-chart-simple__item', {
      [`bar-chart-simple__item--${modifier}`]: modifier.length > 0
    });
    return (
      <div
        key={`bar-chart-simple__item--${item.label}`}
        className={itemContainerClasses}
      >
        {renderItemContents(item, index)}
      </div>
    );
  };

  const renderBarChart = (): React.ReactNode => items.map(renderItem);

  if (isEmpty(items)) {
    return renderEmptyMessage();
  }
  const itemsContainerClasses = classnames('bar-chart-simple__items', {
    [`bar-chart-simple__items--${modifier}`]: modifier.length > 0
  });
  return (
    <div className="bar-chart-simple">
      <div className={itemsContainerClasses}>
        {
          titles && renderTitles()
        }
        {
          renderBarChart()
        }
      </div>
    </div>
  );
};

export default BarChartSimple;
